import { ref, computed, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import { useAgentPermissionOptions } from 'dashboard/permissionSets/useAgentPermissionOptions';
import { agentAssignmentPayload } from 'dashboard/permissionSets/agentAssignment';

export function useAgentInvitation(close) {
  const store = useStore();
  const { t } = useI18n();

  const agentName = ref('');
  const agentEmail = ref('');
  const selectedRoleId = ref('agent');

  const rules = {
    agentName: { required },
    agentEmail: { required, email },
    selectedRoleId: { required },
  };

  const v$ = useVuelidate(rules, {
    agentName,
    agentEmail,
    selectedRoleId,
  });

  const uiFlags = useMapGetter('agents/getUIFlags');
  const {
    roles,
    ownEnabled,
    nativeEnabled,
    enterprise,
    loading: rolesLoading,
    error: rolesError,
    load,
    scope,
  } = useAgentPermissionOptions();
  watch(scope, () => close());
  const selectedRole = computed(() =>
    roles.value.find(role => role.id === selectedRoleId.value)
  );

  const addAgent = async () => {
    v$.value.$touch();
    if (
      v$.value.$invalid ||
      rolesLoading.value ||
      rolesError.value ||
      !selectedRole.value
    )
      return;

    const requestScope = scope.value;
    try {
      const payload = agentAssignmentPayload({
        selection: selectedRoleId.value,
        ownEnabled: ownEnabled.value,
        nativeEnabled: nativeEnabled.value,
        enterprise,
      });
      Object.assign(payload.agent, {
        name: agentName.value,
        email: agentEmail.value,
      });

      const result = await store.dispatch('agents/create', payload);
      if (result === null || scope.value !== requestScope) return;
      useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
      close();
    } catch (error) {
      if (scope.value !== requestScope) return;
      const {
        response: {
          data: {
            error: errorResponse = '',
            attributes: attributes = [],
            message: attrError = '',
          } = {},
        } = {},
      } = error;

      let errorMessage = '';
      if (error?.response?.status === 422 && !attributes.includes('base')) {
        errorMessage = t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
      } else {
        errorMessage = t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
      }
      useAlert(errorResponse || attrError || errorMessage);
    }
  };
  return {
    agentName,
    agentEmail,
    selectedRoleId,
    v$,
    uiFlags,
    roles,
    selectedRole,
    rolesLoading,
    rolesError,
    load,
    addAgent,
  };
}
